import { AxiosRequestConfig } from 'axios';

// Match on the first path segment only — strip leading slashes, then take
// the portion before the first '/', '?', or '#' — to avoid silencing
// unrelated endpoints whose URLs merely contain one of these strings as a
// substring (e.g. 'prompt/history' must NOT be silenced by 'prompt').
const SILENT_FIRST_SEGMENTS = new Set(['suggest', 'status', 'taxonomy', 'dashboards']);
// Multi-segment paths that must be silenced even though their first segment is shared
// with user-visible endpoints (e.g. search/suggest is typeahead; first segment 'search' is not
// silent. search/searchtag is tag search, which is intentionally silent — CHKM-4023: this used
// to live in SILENT_FIRST_SEGMENTS as 'searchtag', which can never match because the URL's first
// segment is 'search', not 'searchtag', so tag search raised the (now click-blocking) global
// overlay on every request instead of staying silent).
const SILENT_PATHS = ['search/suggest', 'search/searchtag'];
// Method+path pairs to silence — POST search/result is graph generation, which has its own snackbar indicator.
const SILENT_METHOD_PATHS: Array<{ method: string; path: string }> = [
  { method: 'post', path: 'search/result' }
];

/**
 * A single source of truth for which requests must not toggle the global loading
 * overlay (CHKM-4023). Shared between the request interceptor (which decides whether
 * to increment `activeRequests`) and the response/error interceptors (which must only
 * decrement it for requests that actually incremented it), so the counter can never go
 * out of sync between a silenced request and its response.
 */
export const isSilentRequest = (config?: AxiosRequestConfig): boolean => {
  if (!config) {
    return false;
  }
  const pathname = (config.url || '').replace(/^\/+/, '').toLowerCase();
  const method = (config.method || '').toLowerCase();
  const seg = pathname.split(/[/?#]/)[0];
  return SILENT_FIRST_SEGMENTS.has(seg)
    || SILENT_PATHS.some(p => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '?'))
    || SILENT_METHOD_PATHS.some(({ method: m, path: p }) => method === m && pathname === p);
};
